import { useMutation, useQueryClient } from "@tanstack/react-query"

import { ApiContext } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  approveSlantaCard,
  approveSlantaCards,
  cancelSlantaPayment,
  createSlantaGift,
  createSlantaOrder,
  destroySlantaGift,
  draftSlantaCard,
  draftSlantaSharePosts,
  prepareSlantaPayment,
  queueSlantaCards,
  submitSlantaOrder,
  updateSlantaGift,
  updateSlantaOrder,
} from "./api"
import {
  SlantaAssignmentRules,
  SlantaGift,
  SlantaGiftAssignmentAction,
  SlantaGiftUpdate,
  SlantaOrder,
  SlantaOrderUpdate,
  SlantaPayment,
  SlantaProductKey,
} from "./types"

function useSlantaMutation<TVariables, TData>(
  mutationFn: (context: ApiContext, variables: TVariables) => Promise<TData>
) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return mutationFn(context, variables)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["slanta", "orders"] }),
        queryClient.invalidateQueries({ queryKey: ["slanta", "availability"] }),
        queryClient.invalidateQueries({ queryKey: ["slanta", "households"] }),
      ])
    },
  })
}

export function useCreateSlantaOrder() {
  return useSlantaMutation<void, { order: SlantaOrder }>((context) => createSlantaOrder(context))
}

export function useUpdateSlantaOrder() {
  return useSlantaMutation<{ id: string; order: SlantaOrderUpdate }, { order: SlantaOrder }>((context, { id, order }) =>
    updateSlantaOrder(context, id, order)
  )
}

export function useApplySlantaAssignmentRules() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      orderId,
      rules,
      actions,
    }: {
      orderId: string
      rules: SlantaAssignmentRules
      actions: SlantaGiftAssignmentAction[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await updateSlantaOrder(context, orderId, { assignment_rules: rules, assignment_actions: actions })
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["slanta", "orders"] }),
        queryClient.invalidateQueries({ queryKey: ["slanta", "availability"] }),
        queryClient.invalidateQueries({ queryKey: ["slanta", "households"] }),
      ])
    },
  })
}

export function useCreateSlantaGift() {
  return useSlantaMutation<
    {
      order_id: string
      household_id: string
      product_key: SlantaProductKey
      assignment_source?: SlantaGift["assignment_source"]
    },
    { gift: SlantaGift }
  >((context, gift) => createSlantaGift(context, gift))
}

export function useUpdateSlantaGift() {
  return useSlantaMutation<{ id: string; gift: SlantaGiftUpdate }, { gift: SlantaGift }>((context, { id, gift }) =>
    updateSlantaGift(context, id, gift)
  )
}

export function useDestroySlantaGift() {
  return useSlantaMutation<string, void>((context, id) => destroySlantaGift(context, id))
}

export function useDraftSlantaCard() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation({
    mutationFn: async ({
      giftId,
      instruction,
    }: {
      giftId: string
      instruction: "draft" | "rewrite" | "shorter" | "warmer"
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return draftSlantaCard(context, giftId, instruction)
    },
    onSuccess: ({ gift }) => {
      queryClient.setQueryData<{ orders: SlantaOrder[] }>(
        ["slanta", "orders", bookId],
        (current) =>
          current && {
            orders: current.orders.map((order) => ({
              ...order,
              gifts: order.gifts.map((existing) =>
                existing.id === gift.id && existing.updated_at <= gift.updated_at ? gift : existing
              ),
            })),
          }
      )
    },
  })
}

export function useApproveSlantaCard() {
  return useSlantaMutation<string, { gift: SlantaGift }>((context, giftId) => approveSlantaCard(context, giftId))
}

export function useApproveSlantaCards() {
  return useSlantaMutation<{ orderId: string; gifts: Pick<SlantaGift, "id" | "message">[] }, { order: SlantaOrder }>(
    (context, { orderId, gifts }) => approveSlantaCards(context, orderId, gifts)
  )
}

export function useSubmitSlantaOrder() {
  return useSlantaMutation<string, { order: SlantaOrder }>((context, orderId) => submitSlantaOrder(context, orderId))
}

export function usePrepareSlantaPayment() {
  return useSlantaMutation<string, { payment: SlantaPayment }>((context, orderId) =>
    prepareSlantaPayment(context, orderId)
  )
}

export function useCancelSlantaPayment() {
  return useSlantaMutation<{ orderId: string; checkoutAttempt: number }, { order: SlantaOrder }>(
    (context, { orderId, checkoutAttempt }) => cancelSlantaPayment(context, orderId, checkoutAttempt)
  )
}

export function useQueueSlantaCards() {
  return useSlantaMutation<{ orderId: string; retryFailed?: boolean }, { order: SlantaOrder }>(
    (context, { orderId, retryFailed = false }) => queueSlantaCards(context, orderId, retryFailed)
  )
}

export function useDraftSlantaSharePosts() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()
  return useMutation({
    mutationFn: async ({ orderId, regenerate = false }: { orderId: string; regenerate?: boolean }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return draftSlantaSharePosts(context, orderId, regenerate)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["slanta", "orders", bookId] })
    },
  })
}
